import type { FloorDef } from '../types';
import { room, door, stairs } from '../dsl';

/**
 * 1층 — 현관홀을 중심으로 서쪽(서재·응접실·당구장), 동쪽(식당·주방), 북쪽(무도회장·온실·회랑)으로 뻗는다.
 * Grid 128 x 80.
 */
export const GROUND: FloorDef = {
  id: 'ground',
  w: 128,
  h: 80,
  rooms: [
    room('hall', '현관홀', 52, 50, 24, 19, 'hall', 'marble', 'cream', {
      lights: [{ x: 64 * 16, y: 68 * 16, r: 70, color: '#5a6fb0', cone: { angle: -Math.PI / 2, half: 1.0 }, id: 'hall-moon' }],
    }),
    room('corridor', '중앙 복도', 30, 45, 93, 4, 'corridor', 'carpetRed', 'green'),
    room('westcorr', '서쪽 복도', 30, 10, 4, 34, 'corridor', 'carpetRed', 'green'),
    room('gallery1', '북쪽 회랑', 52, 17, 71, 4, 'corridor', 'carpetBlue', 'blue'),
    room('trophy', '사냥 전리품실', 35, 10, 16, 11, 'trophy', 'wood', 'panel'),
    room('smoking', '흡연실', 35, 22, 16, 9, 'smoking', 'carpetGreen', 'panel'),
    room('music', '음악실', 35, 32, 16, 12, 'music', 'wood', 'blue', { windows: 0 }),
    room('ballroom', '무도회장', 52, 22, 46, 22, 'ballroom', 'woodPale', 'red', { windows: 0.7 }),
    room('conservatory', '온실', 99, 22, 24, 22, 'conservatory', 'marble', 'plaster', { windows: 1 }),
    room('chapel', '예배당', 6, 10, 23, 21, 'chapel', 'stone', 'stone', { windows: 0.4 }),
    room('secret', '비밀 서고', 6, 32, 10, 11, 'secret', 'plank', 'brick'),
    room('wstore', '서쪽 창고', 17, 32, 12, 11, 'storage', 'plank', 'plaster'),
    room('library', '서재', 6, 44, 23, 20, 'library', 'wood', 'panel', {
      windows: 0,
      props: [
        { kind: 'fireplace', x: 14, y: 44, id: 'lib-fire' },
        { kind: 'bookshelfLever', x: 7, y: 44, id: 'lib-lever-a' },
        { kind: 'bookshelfLever', x: 10, y: 44, id: 'lib-lever-b' },
        { kind: 'bookshelfLever', x: 19, y: 44, id: 'lib-lever-c' },
        { kind: 'bookshelfLever', x: 22, y: 44, id: 'lib-lever-d' },
        { kind: 'bookshelfLever', x: 25, y: 44, id: 'lib-lever-e' },
      ],
    }),
    room('wpass', '서쪽 통로', 30, 50, 4, 14, 'passage', 'plank', 'plaster'),
    room('drawing', '응접실', 35, 50, 16, 14, 'drawing', 'carpetRed', 'red'),
    room('billiard', '당구실', 6, 65, 28, 12, 'billiard', 'carpetGreen', 'panel'),
    room('cloak', '외투 보관실', 35, 65, 16, 7, 'cloak', 'wood', 'cream'),
    room('washroom', '화장실', 35, 73, 16, 5, 'washroom', 'checker', 'plaster'),
    room('dining', '식당', 77, 50, 22, 14, 'dining', 'wood', 'red'),
    room('kitchen', '주방', 100, 50, 23, 15, 'kitchen', 'checker', 'plaster'),
    room('pantry', '식료품 저장실', 100, 66, 11, 7, 'pantry', 'plank', 'plaster', { prison: true }),
    room('backstairs', '하인 계단실', 112, 66, 11, 10, 'stairhall', 'plank', 'plaster'),
    room('hunting', '사냥 도구실', 52, 6, 19, 10, 'trophy', 'wood', 'panel'),
    room('waiting', '응접 대기실', 72, 6, 19, 10, 'smoking', 'carpetBlue', 'blue', { windows: 0.5 }),
    room('office', '관리인 사무실', 92, 6, 19, 10, 'study', 'wood', 'green', { windows: 0.5 }),
    room('storeroom', '북쪽 창고', 112, 6, 11, 10, 'storage', 'plank', 'plaster'),
  ],
  doors: [
    door('hall', 'outside', 'exit', { id: 'front-door', label: '정문', key: 'key_master' }),
    door('hall', 'corridor', 'arch'),
    door('hall', 'drawing', 'door', { at: 0.3 }),
    door('hall', 'dining', 'door', { at: 0.3 }),
    door('hall', 'cloak', 'door'),
    door('corridor', 'westcorr', 'open'),
    door('corridor', 'library', 'door'),
    door('corridor', 'wpass', 'open'),
    door('corridor', 'drawing', 'door', { at: 0.7 }),
    door('corridor', 'ballroom', 'double'),
    door('corridor', 'music', 'door'),
    door('corridor', 'conservatory', 'door', { at: 0.3 }),
    door('corridor', 'dining', 'door', { at: 0.6 }),
    door('corridor', 'kitchen', 'boarded', { id: 'kitchen-boarded', label: '판자로 막힌 문' }),
    door('westcorr', 'chapel', 'door', { at: 0.5 }),
    door('westcorr', 'trophy', 'door'),
    door('westcorr', 'smoking', 'door'),
    door('westcorr', 'music', 'door'),
    door('westcorr', 'wstore', 'door'),
    door('trophy', 'gallery1', 'door'),
    door('gallery1', 'ballroom', 'double'),
    door('gallery1', 'conservatory', 'door', { at: 0.6 }),
    door('gallery1', 'hunting', 'door'),
    door('gallery1', 'waiting', 'door'),
    door('gallery1', 'office', 'door'),
    door('gallery1', 'storeroom', 'boarded', { id: 'storeroom-boarded', label: '판자로 막힌 문' }),
    door('ballroom', 'conservatory', 'door', { at: 0.5 }),
    door('ballroom', 'music', 'door', { at: 0.5 }),
    door('library', 'secret', 'secret', { id: 'library-secret', trigger: 'lever_library', at: 0.5 }),
    door('library', 'wstore', 'door', { at: 0.5 }),
    door('library', 'wpass', 'door', { at: 0.4 }),
    door('library', 'billiard', 'door', { at: 0.5 }),
    door('wpass', 'drawing', 'door', { at: 0.5 }),
    door('wpass', 'billiard', 'door'),
    door('drawing', 'cloak', 'door', { at: 0.5 }),
    door('cloak', 'washroom', 'door', { at: 0.3 }),
    door('billiard', 'washroom', 'door'),
    door('dining', 'kitchen', 'locked', { id: 'kitchen-door', key: 'key_kitchen', label: '주방 문', at: 0.5 }),
    door('kitchen', 'pantry', 'bolt', { id: 'pantry-bolt', label: '저장실 문', at: 0.5 }),
    door('kitchen', 'backstairs', 'door', { at: 0.5 }),
    door('smoking', 'music', 'door', { at: 0.5 }),
  ],
  stairs: [
    stairs('grand1', 'hall', 62, 50, 'up', 'S', 'second', 'grand2', { w: 4, h: 3 }),
    stairs('back1d', 'backstairs', 113, 73, 'down', 'N', 'basement', 'backb'),
    stairs('back1u', 'backstairs', 120, 66, 'up', 'S', 'second', 'back2'),
    stairs('store1d', 'storeroom', 113, 6, 'down', 'S', 'basement', 'coalb'),
  ],
};
